/**
 * Report a problem — Phase 1 support intake (design doc: "Phase 1 Support
 * Intake — Example Ticket v3").
 *
 * Data: unbound. One declared write, `createSupportReport` →
 * POST /v1/support/reports, which exists in no contract and therefore FAILS on
 * every send today. Operator ruling 2026-09-21 (Nick): let it fail, show the
 * real error, and give the reporter Retry plus "Copy report as text" rather
 * than a success toast with nothing behind it. The confirmation state (Figure
 * 4) is built and unreachable in normal use; `?supportReportDemo=success`
 * forces it for review. REQ-SR-01.
 *
 * THE DESIGN IN ONE LINE: no free-text box anywhere, so there is no field a
 * customer's name can be typed into. Everything the reporter can answer is a
 * fixed vocabulary we wrote ourselves (`vocabularies.ts`) or an identifier from
 * our own component tree (`ElementPicker`). That is what makes the PII-free
 * claim structural rather than a promise, and it is the reason this dialog has
 * more radio buttons than any other screen in the app.
 *
 * ENTRY POINTS (all four operator-chosen): the profile dropdown and the help
 * menu in `chrome-topbar.tsx`, the "Report this" action on an error toast
 * (`toast.tsx`), and Alt+R anywhere.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Btn, Icon, Modal, T, alpha } from "@v0y4ge/jrni-ui";
import { useNav } from "../shell/router";
import { useUser, useTenant, useImpersonation, canReportProblem } from "../shell/access";
import { setToastReportHandler } from "../shell/toast";
import { useT } from "../lib/i18n";
import { ElementPicker, safeIdentifier } from "./ElementPicker";
import {
  browserLabel, clearCapture, countReportFiled, lastFailure, noteUiEvent, recentUiEvents, renderReportText,
  resetCaptureForTenant,
  sessionErrorCodes, submitSupportReport,
  type PickedControl, type SupportReportPayload,
} from "./capture";
import {
  FREQUENCIES, LAST_WORKED, NONE_OF_THESE, OUTCOMES, SOMETHING_ELSE, vocabularyForRoute,
  type VocabRow,
} from "./vocabularies";

/** English label for a picked id. Deliberately the raw `en` literal, not
 *  `t(key, en)`: the ticket is read by support, so it must not follow the
 *  reporter's locale. */
function labelOf(rows: VocabRow[], id: string | null): string | null {
  if (!id) return null;
  return rows.find((r) => r.id === id)?.en ?? null;
}

/* ── open/close store ──────────────────────────────────────────────────────
   Module-level so any surface can open the dialog without prop-drilling —
   same pattern as toast.tsx. `source` is recorded so we can tell an ambient
   report (profile menu) from a contextual one (error toast); they behave
   differently and support should know which they are reading. */
type ReportSource = "profileMenu" | "helpMenu" | "errorToast" | "shortcut";
let _open: ReportSource | null = null;
const _subs = new Set<() => void>();
const _emit = () => _subs.forEach((f) => { try { f(); } catch { /* noop */ } });

export function openReportProblem(source: ReportSource = "profileMenu"): void {
  /* Gate here as well as at every entry point. The menu items are hidden
     without the capability, but a module-level opener is reachable from
     anywhere and must not be the hole in the fence. */
  if (!canReportProblem()) return;
  _open = source;
  noteUiEvent("dialog", `support.report.open:${source}`);
  _emit();
}
function closeReportProblem(): void { _open = null; _emit(); }

/* ── small presentational parts (ui-raw, deliberately) ─────────────────────
   Chips and the segmented rows below are NOT jrni-ui primitives: Pill is a
   status display with no selected state or press affordance, and there is no
   ChoiceChip / SegmentedControl in the library. Raw controls, marked as such,
   rather than bending a display component into a control. */
function Chip({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={selected} style={{
      border: `${selected ? 1.5 : 1}px solid ${selected ? T.p1 : T.border}`,
      background: selected ? T.p3 : T.white, color: selected ? T.p1 : T.n2,
      fontSize: 13, fontWeight: selected ? 500 : 400, borderRadius: 999,
      padding: "6px 13px", cursor: "pointer", minHeight: 32,
    }}>{children}</button>
  );
}

function Segment({ rows, value, onChange }: { rows: VocabRow[]; value: string | null; onChange: (id: string) => void }) {
  const t = useT();
  return (
    <div style={{ display: "flex", border: `1px solid ${T.border}`, borderRadius: 6, overflow: "hidden" }}>
      {rows.map((r, i) => (
        <button key={r.id} type="button" onClick={() => onChange(r.id)} aria-pressed={value === r.id} style={{
          flex: 1, textAlign: "center", fontSize: 13, padding: "9px 6px", cursor: "pointer",
          border: "none", borderLeft: i ? `1px solid ${T.border}` : "none",
          background: value === r.id ? T.p1 : T.white, color: value === r.id ? T.onColor : T.n2,
          fontWeight: value === r.id ? 500 : 400, minHeight: 36,
        }}>{t(r.key, r.en)}</button>
      ))}
    </div>
  );
}

function Radio({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={selected} style={{
      display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left",
      border: `${selected ? 1.5 : 1}px solid ${selected ? T.p1 : T.border}`,
      background: selected ? T.p3 : T.white, borderRadius: 6, padding: "11px 12px", cursor: "pointer",
    }}>
      <span style={{
        width: 16, height: 16, borderRadius: 999, flexShrink: 0, background: T.white,
        border: selected ? `5px solid ${T.p1}` : `1.5px solid ${T.soft}`,
      }} />
      <span style={{ fontSize: 14, color: T.n2 }}>{children}</span>
    </button>
  );
}

function SectionLabel({ children, note }: { children: ReactNode; note?: ReactNode }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{children}</div>
      {note ? <div style={{ fontSize: 12, color: T.soft, marginTop: 3 }}>{note}</div> : null}
    </div>
  );
}

/* ── the dialog ───────────────────────────────────────────────────────────*/
function ReportDialog({ source, onClose }: { source: ReportSource; onClose: () => void }) {
  const t = useT();
  const nav = useNav();
  const user = useUser();
  const tenant = useTenant();
  const impersonation = useImpersonation();

  const vocab = useMemo(() => vocabularyForRoute(nav.route), [nav.route]);
  const failure = useMemo(() => lastFailure(), []);
  const errorCodes = useMemo(() => sessionErrorCodes(), []);
  const openedAt = useRef(Date.now());

  const demoSuccess = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("supportReportDemo") === "success";

  const [step, setStep] = useState<"intent" | "detail" | "sent" | "failed">(demoSuccess ? "sent" : "intent");
  const [sending, setSending] = useState(false);
  const [reference, setReference] = useState<string | null>(demoSuccess ? "REP-DEMO01" : null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyText, setCopyText] = useState<string | null>(null);

  const [intent, setIntent] = useState<string | null>(null);
  const [area, setArea] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [messageCode, setMessageCode] = useState<string | null>(null);
  const [frequency, setFrequency] = useState<string | null>(null);
  const [lastWorked, setLastWorked] = useState<string | null>(null);
  const [picked, setPicked] = useState<PickedControl | null>(null);
  const [picking, setPicking] = useState(false);
  const [filedThisSession, setFiledThisSession] = useState(1);

  const payload: SupportReportPayload = useMemo(() => ({
    source,
    intent, area, outcome, frequency, lastWorked,
    labels: {
      intent: labelOf([...vocab.intents, SOMETHING_ELSE], intent),
      area: labelOf([...vocab.areas, NONE_OF_THESE], area),
      outcome: labelOf(OUTCOMES, outcome),
      frequency: labelOf(FREQUENCIES, frequency),
      lastWorked: labelOf(LAST_WORKED, lastWorked),
    },
    vocabularyFamily: vocab.family,
    messageCode,
    pointedAt: picked,
    failure,
    screen: {
      route: nav.route,
      navPath: [nav.prevRoute, nav.route].filter((x): x is string => !!x),
      secondsOnScreen: Math.round((Date.now() - openedAt.current) / 1000),
    },
    reporter: {
      role: user?.role || "unknown",
      userId: (user as { id?: string } | null)?.id || null,
      locale: typeof navigator !== "undefined" ? navigator.language : "unknown",
      impersonated: !!impersonation,
    },
    tenant: { slug: tenant?.slug || "unknown", plan: tenant?.plan || "unknown" },
    client: {
      browser: browserLabel(),
      viewport: typeof window !== "undefined" ? `${window.innerWidth}×${window.innerHeight}` : "unknown",
    },
    recentEvents: recentUiEvents(),
    /* Seed-served data marks itself (`__source: "cd-preview-seed"`); without
       stamping it here support investigates bugs that only exist in a demo. */
    previewData: typeof window !== "undefined" && !!(window as unknown as { __CD_PREVIEW__?: boolean }).__CD_PREVIEW__,
    occurrenceThisSession: filedThisSession,
  }), [source, intent, area, outcome, frequency, lastWorked, vocab.family, vocab.intents, vocab.areas, messageCode, picked, failure, nav.route, nav.prevRoute, user, impersonation, tenant, filedThisSession]);

  const send = useCallback(async () => {
    setSending(true);
    setSendError(null);
    setFiledThisSession(countReportFiled());
    try {
      const res = await submitSupportReport(payload);
      setReference(res.reference);
      /* Audit emission. Part 0 of the data model makes this non-negotiable and
         23 audit operations already exist, so a filed report is as auditable
         as any other write. Recorded on SUCCESS only — a failed attempt is not
         a filed report, and an audit trail that logs intentions stops being
         evidence. The authoritative event is the server's (REQ-SR-01a); this
         is the client half, and it carries no more than the email does. */
      try {
        /* Loaded at the CALL SITE, not imported at the top.
           `lib/audit` runs `onTenantChange()` at module scope, and this module
           is reachable from `shell/toast`, which `access.ts` itself imports —
           a static import here put audit.ts inside an access-rooted cycle and
           executed it before access.ts had initialised its subscriber set.
           The whole app rendered an error boundary. Deferring the load keeps
           audit.ts out of the synchronous graph entirely. */
        const { logAction } = await import("../lib/audit");
        logAction("supportReport", res.reference, `Problem report ${res.reference}`, "filed", {
          source: "web",
          changes: [
            { field: "screen", from: null, to: payload.screen.route },
            { field: "intent", from: null, to: payload.labels.intent ?? payload.intent },
            { field: "outcome", from: null, to: payload.labels.outcome ?? payload.outcome },
            { field: "operation", from: null, to: payload.failure?.operationId ?? "none captured" },
          ],
        });
      } catch { /* audit must never break the thing it records */ }
      setStep("sent");
    } catch (err) {
      /* The REAL failure, rendered. No optimistic success anywhere — and the
         thing being reported is often the API the report travels over. */
      const e = err as { code?: unknown; message?: unknown; status?: unknown };
      const code = typeof e?.code === "string" ? e.code : null;
      const status = typeof e?.status === "number" ? e.status : null;
      setSendError([code, status ? `HTTP ${status}` : null].filter(Boolean).join(" · ") || (typeof e?.message === "string" ? e.message : "The report could not be sent."));
      setStep("failed");
    } finally {
      setSending(false);
    }
  }, [payload]);

  /**
   * Copy the report.
   *
   * THE FAILURE MODE THIS EXISTS FOR (review, 2026-09-21: "copy text is still
   * only pulling the url of the page"): `navigator.clipboard.writeText` is
   * blocked outright in a cross-origin iframe and in any non-secure context.
   * The first version caught that and set `copied` false — which looked like
   * nothing happened, so the reporter pasted whatever was on their clipboard
   * already (the page URL) and reasonably concluded we had copied it.
   *
   * Three rungs, and the last one cannot fail: the async clipboard, the legacy
   * `execCommand` path, then showing the text in a selectable box with a
   * select-all so they can copy it by hand. A report the reporter cannot get
   * out of the dialog is the same as no report.
   */
  const copy = useCallback(() => {
    const text = renderReportText(payload, reference);
    void (async () => {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          return;
        }
      } catch { /* blocked — fall through */ }
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.cssText = "position:fixed;top:0;left:0;opacity:0;pointer-events:none";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (ok) { setCopied(true); return; }
      } catch { /* fall through */ }
      setCopyText(text);
    })();
  }, [payload, reference]);

  /* "Copied" is a momentary acknowledgement, not a state the button lives in.
     Left latched it mislabels the NEXT copy — after a second send failure the
     reporter is asked to press a button that says it has already done the
     thing. Clears on a timer and whenever the step changes. */
  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(false), 2500);
    return () => window.clearTimeout(id);
  }, [copied]);
  useEffect(() => { setCopied(false); setCopyText(null); }, [step]);

  /* Rendered when both clipboard paths are unavailable. Deliberately the WHOLE
     message body, not a summary: this is the thing they are being asked to
     send on manually. */
  const copyFallback = copyText ? (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 12.5, color: T.n2, marginBottom: 6, lineHeight: 1.5 }}>
        {t("support.report.copyManual", "Your browser wouldn't let us write to the clipboard. Here is the whole report — select it and copy.")}
      </div>
      {/* ui-raw: a read-only transcript box. Not TextArea (an input primitive
          with label/validation affordances) — nothing here is editable. */}
      <textarea
        readOnly
        value={copyText}
        onFocus={(e) => e.currentTarget.select()}
        rows={12}
        style={{
          width: "100%", boxSizing: "border-box", fontFamily: "ui-monospace, Menlo, monospace",
          fontSize: 11.5, lineHeight: 1.6, color: T.n2, background: T.n6,
          border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 12px", resize: "vertical",
        }}
      />
    </div>
  ) : null;

  if (picking) {
    return <ElementPicker onPick={(c) => { setPicked(c); setPicking(false); noteUiEvent("picker", safeIdentifier(c.testid) || c.component); }} onCancel={() => setPicking(false)} />;
  }

  const contextChip = (
    <span style={{
      display: "inline-block", background: T.n6, border: `1px solid ${T.border}`, borderRadius: 999,
      padding: "5px 12px", fontSize: 12, color: T.n2,
    }}>
      {failure
        ? t("support.report.contextWithError", "Reporting from this page · a failed request was captured")
        : t("support.report.contextNoError", "Reporting from this page · no failed request was captured")}
    </span>
  );

  /* ── Figure 4 — sent ─────────────────────────────────────────────────── */
  if (step === "sent") {
    return (
      <Modal open onClose={onClose} maxWidth={520} title={t("support.report.sentTitle", "Report sent")}>
        <Modal.Body>
          <div style={{ display: "flex", gap: 13, alignItems: "flex-start" }}>
            <span style={{ width: 28, height: 28, borderRadius: 999, background: T.green1, color: T.onColor, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.check size={15} /></span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, color: T.n2, lineHeight: 1.6, marginBottom: 12 }}>
                {t("support.report.sentBody", "A support engineer will look at this and come back to you. Nothing about any customer was sent, so you don't need to check anything before you carry on.")}
              </div>
              <div style={{ background: T.n6, border: `1px solid ${T.border}`, borderRadius: 8, padding: "11px 13px", fontSize: 13, color: T.n2 }}>
                {t("support.report.reference", "Reference")} <strong style={{ fontFamily: "ui-monospace, Menlo, monospace" }}>{reference}</strong>
              </div>
              {copyFallback}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Btn variant="secondary" onClick={copy}>{copied ? t("support.report.copied", "Copied") : t("support.report.copy", "Copy report as text")}</Btn>
          <Btn onClick={onClose}>{t("support.report.backToWork", "Back to what I was doing")}</Btn>
        </Modal.Footer>
      </Modal>
    );
  }

  /* ── send failed ─────────────────────────────────────────────────────── */
  if (step === "failed") {
    return (
      <Modal open onClose={onClose} maxWidth={520} title={t("support.report.failedTitle", "The report couldn't be sent")}>
        <Modal.Body>
          <div style={{ border: `1px solid ${alpha(T.red1, 28)}`, background: T.red2, borderRadius: 8, padding: "12px 14px", marginBottom: 14 }}>
            <div style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
              <span style={{ color: T.red1, flexShrink: 0, marginTop: 1 }}><Icon.warn size={16} /></span>
              <div style={{ fontSize: 13.5, color: T.n2, lineHeight: 1.6 }}>
                {t("support.report.failedBody", "Your answers are still here. The thing you're reporting is often the same service the report travels over, so this can happen exactly when you need it least.")}
                <div style={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12, color: T.soft, marginTop: 7 }}>{sendError}</div>
              </div>
            </div>
          </div>
          <div style={{ fontSize: 13, color: T.soft, lineHeight: 1.6 }}>
            {t("support.report.copyHint", "You can copy the report as text and send it however you normally raise things — it contains no customer details, so it's safe to paste anywhere.")}
          </div>
          {copyFallback}
        </Modal.Body>
        <Modal.Footer>
          <Btn variant="ghost" onClick={() => setStep("detail")}>{t("support.report.back", "Back")}</Btn>
          <Btn variant="secondary" onClick={copy}>{copied ? t("support.report.copied", "Copied") : t("support.report.copy", "Copy report as text")}</Btn>
          <Btn onClick={() => void send()} disabled={sending}>{t("support.report.retry", "Try again")}</Btn>
        </Modal.Footer>
      </Modal>
    );
  }

  /* ── Figure 1 — intent ───────────────────────────────────────────────── */
  if (step === "intent") {
    const intents = [...vocab.intents, SOMETHING_ELSE];
    return (
      <Modal open onClose={onClose} maxWidth={560}
        title={t("support.report.title", "Report a problem")}
        description={failure
          ? t("support.report.subtitleWithError", "We've captured the error. Just tell us what you were trying to do.")
          : t("support.report.subtitleNoError", "Nothing failed that we could see, so tell us what you were trying to do.")}>
        <Modal.Body>
          <div style={{ marginBottom: 14 }}>{contextChip}</div>
          <div style={{ display: "grid", gap: 8, marginBottom: 16 }}>
            {intents.map((r) => (
              <Radio key={r.id} selected={intent === r.id} onClick={() => {
                setIntent(r.id);
                /* "Something else" on its own tells us nothing, so it opens the
                   ladder rather than letting the reporter send a blank. */
                if (r.id === SOMETHING_ELSE.id) setStep("detail");
              }}>{t(r.key, r.en)}</Radio>
            ))}
          </div>
          <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, background: T.n6, padding: "14px 15px" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.n1, marginBottom: 7 }}>{t("support.report.whatWeSend", "What gets sent with this report")}</div>
            <div style={{ fontSize: 13, color: T.n2, lineHeight: 1.75 }}>
              <div>{t("support.report.send1", "The page you're on and the button you pressed")}</div>
              <div>{t("support.report.send2", "The failed request and its error code, if there was one")}</div>
              <div>{t("support.report.send3", "Your browser, and which version of the app you're using")}</div>
              <div>{t("support.report.send4", "Anything you pick if you add more detail")}</div>
            </div>
            <div style={{ borderTop: `1px solid ${T.border}`, margin: "12px 0 10px" }} />
            <div style={{ fontSize: 13, color: T.n2, lineHeight: 1.6 }}>
              <strong>{t("support.report.noCustomerData", "No customer details are included")}</strong>
              {" "}
              {t("support.report.noCustomerDataBody", "— not their name, contact details, notes or appointments, and no screenshot. If our engineers need more, they'll ask you and you decide what to share.")}
            </div>
          </div>
          {impersonation ? (
            <div style={{ marginTop: 12, border: `1px solid ${alpha(T.orange1, 28)}`, borderRadius: 8, background: T.orange2, padding: "10px 12px", fontSize: 12.5, color: T.n2, lineHeight: 1.55 }}>
              {t("support.report.impersonationNotice", "You're acting as someone else right now. This report will say so, and will carry both your id and theirs.")}
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Btn variant="ghost" onClick={onClose}>{t("support.report.cancel", "Cancel")}</Btn>
          <Btn variant="secondary" onClick={() => setStep("detail")}>{t("support.report.addDetail", "Add more detail")}</Btn>
          <Btn onClick={() => void send()} disabled={!intent || sending}>{sending ? t("support.report.sending", "Sending…") : t("support.report.send", "Send report")}</Btn>
        </Modal.Footer>
      </Modal>
    );
  }

  /* ── Figure 2 — the detail ladder ────────────────────────────────────── */
  const areas = [...vocab.areas, NONE_OF_THESE];
  return (
    <Modal open onClose={onClose} maxWidth={620}
      title={t("support.report.detailTitle", "A bit more detail")}
      description={t("support.report.detailSubtitle", "Pick whatever applies. Nothing here is typed, so nothing here can carry customer details.")}>
      <Modal.Body>
        <div style={{ display: "grid", gap: 18 }}>
          <div>
            <SectionLabel note={vocab.family === "generic"
              ? t("support.report.areaNoteGeneric", "We don't have a tailored list for this page yet — “None of these” tells us it's missing.")
              : t("support.report.areaNote", "“None of these” is useful too — it means we've modeled this page wrong.")}>
              {t("support.report.areaQuestion", "Which part of this page?")}
            </SectionLabel>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {areas.map((r) => <Chip key={r.id} selected={area === r.id} onClick={() => setArea(r.id)}>{t(r.key, r.en)}</Chip>)}
            </div>
          </div>

          <div>
            <SectionLabel>{t("support.report.outcomeQuestion", "What went wrong?")}</SectionLabel>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {OUTCOMES.map((r) => <Chip key={r.id} selected={outcome === r.id} onClick={() => setOutcome(r.id)}>{t(r.key, r.en)}</Chip>)}
            </div>
          </div>

          {/* "Was it one of these messages?" — sourced from the error CODES seen
              this session, not from the i18n catalog. The catalog version in the
              design doc needs error copy moved into `errors.*` first (it is
              effectively one key today); codes are real now and catalog keys
              fold in beside them as they land (operator ruling, "both"). */}
          {errorCodes.length ? (
            <div>
              <SectionLabel note={t("support.report.messageNote", "Error codes this session. We send which one you picked, never the message as it appeared on your screen.")}>
                {t("support.report.messageQuestion", "Was it one of these?")}
              </SectionLabel>
              <div style={{ display: "grid", gap: 7 }}>
                {errorCodes.map((e) => (
                  <Radio key={e.at + e.code} selected={messageCode === e.code} onClick={() => setMessageCode(e.code)}>
                    <span style={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12.5 }}>{e.code}</span>
                    <span style={{ color: T.soft, fontSize: 12.5 }}>{"  ·  "}{e.operationId}</span>
                  </Radio>
                ))}
                <Radio selected={messageCode === "__none"} onClick={() => setMessageCode("__none")}>
                  {t("support.report.messageNone", "Something else, or I don't remember")}
                </Radio>
              </div>
            </div>
          ) : null}

          <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))" }}>
            <div>
              <SectionLabel>{t("support.report.frequencyQuestion", "How often?")}</SectionLabel>
              <Segment rows={FREQUENCIES} value={frequency} onChange={setFrequency} />
            </div>
            <div>
              <SectionLabel>{t("support.report.lastWorkedQuestion", "When did it last work?")}</SectionLabel>
              <Segment rows={LAST_WORKED} value={lastWorked} onChange={setLastWorked} />
            </div>
          </div>

          <div style={{ border: `1px solid ${T.border}`, background: T.n6, borderRadius: 8, padding: "14px 15px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div style={{ maxWidth: 400 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.n1, marginBottom: 3 }}>{t("support.report.pickerTitle", "Show us where")}</div>
                <div style={{ fontSize: 12.5, color: T.soft, lineHeight: 1.5 }}>
                  {t("support.report.pickerBody", "Click the thing that's wrong. We record which control it is, not what it says.")}
                </div>
              </div>
              <Btn variant="secondary" size="sm" onClick={() => setPicking(true)}>
                {picked ? t("support.report.pickAgain", "Point at something else") : t("support.report.point", "Point at it")}
              </Btn>
            </div>
            {picked ? (
              <div style={{ marginTop: 11, borderTop: `1px solid ${T.border}`, paddingTop: 11, fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12, color: T.n2, lineHeight: 1.7 }}>
                <div>{t("support.report.pickedComponent", "component")} {picked.component}</div>
                {picked.testid ? <div>{t("support.report.pickedTestid", "testid")} {picked.testid}</div> : null}
                {picked.field ? <div>{t("support.report.pickedField", "field")} {picked.field}</div> : null}
                <div>{t("support.report.pickedRole", "role")} {picked.role}{picked.disabled ? ` · ${t("support.report.pickedDisabled", "disabled")}` : ""}{picked.fieldPosition ? ` · ${t("support.report.pickedFieldPosition", "{index} of {total} fields", { index: picked.fieldPosition.index, total: picked.fieldPosition.total })}` : ""}</div>
                <div>{t("support.report.pickedPath", "path")} {picked.componentPath}</div>
                <div>{t("support.report.pickedId", "id")} {picked.signature}</div>
                {picked.attrs.length ? <div>{t("support.report.pickedAttrs", "attrs")} {picked.attrs.join(" · ")}</div> : null}
                <div style={{ fontFamily: T.font, color: T.soft, marginTop: 6, lineHeight: 1.5 }}>
                  {t("support.report.picked", "Recorded: what this control is, where it sits and what state it's in — never what it says or what's in it.")}
                </div>
              </div>
            ) : null}
          </div>

          {impersonation ? (
            <div style={{ border: `1px solid ${alpha(T.orange1, 28)}`, borderRadius: 8, background: T.orange2, padding: "10px 12px", fontSize: 12.5, color: T.n2, lineHeight: 1.55 }}>
              {t("support.report.impersonationNotice", "You're acting as someone else right now. This report will say so, and will carry both your id and theirs.")}
            </div>
          ) : null}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Btn variant="ghost" onClick={() => setStep("intent")}>{t("support.report.back", "Back")}</Btn>
        <Btn onClick={() => void send()} disabled={sending}>{sending ? t("support.report.sending", "Sending…") : t("support.report.send", "Send report")}</Btn>
      </Modal.Footer>
    </Modal>
  );
}

/**
 * Host — mounted once inside the signed-in branch of `AuthGate`.
 *
 * Owns three things besides rendering: the Alt+R shortcut, the route-change
 * trail the report carries, and closing the dialog on navigation (the picker
 * needs the live screen behind it, so the user can still move underneath).
 * Unmounting = signing out, which clears the capture buffer — a shared branch
 * terminal must not hand the next person the last one's session trail.
 */
export function ReportProblemHost() {
  const [, force] = useState(0);
  const nav = useNav();
  const tenant = useTenant();
  const tenantSlug = tenant?.slug || "";
  const seenTenant = useRef(tenantSlug);

  useEffect(() => {
    const f = () => force((n) => n + 1);
    _subs.add(f);
    return () => { _subs.delete(f); };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey && !e.ctrlKey && !e.metaKey && (e.key === "r" || e.key === "R")) {
        e.preventDefault();
        openReportProblem("shortcut");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => { noteUiEvent("route", nav.route); }, [nav.route]);
  /* Register the error-toast action here rather than letting toast.tsx import
     this module — see the seam note in shell/toast.tsx. Withdrawn on unmount
     (sign-out), so a signed-out toast never offers it. */
  useEffect(() => {
    setToastReportHandler(canReportProblem() ? () => openReportProblem("errorToast") : null);
    return () => setToastReportHandler(null);
  }, []);
  /* Switching workspace forgets the previous tenant's trail before a single
     new event lands — see resetCaptureForTenant(). */
  useEffect(() => {
    if (seenTenant.current === tenantSlug) return;
    seenTenant.current = tenantSlug;
    resetCaptureForTenant();
    if (_open) closeReportProblem();
  }, [tenantSlug]);
  // Closes on navigation (operator ruling) — the report is about the screen it
  // was opened on, and a dialog that outlives it would describe the wrong one.
  useEffect(() => { if (_open) closeReportProblem(); }, [nav.route]);
  useEffect(() => () => { clearCapture(); }, []);

  if (!_open) return null;
  return <ReportDialog source={_open} onClose={closeReportProblem} />;
}
